import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import {
  getTableNames,
  mapTablesToSelection,
} from 'new/components/flows/types/elt/CreateELTFlow/DestinationSetupPage/getTablesFromResource';
import { getMapping } from 'new/components/flows/types/elt/CreateELTFlow/DestinationSetupPage/getMappings';
import { isUiModeCode } from 'new/utils/uiMode';

/**
 * @DOC
 * This function applies form changes to the saved config and keeps all other fields.
 * Previously absent objects stay absent until edited.
 * Editing one stores the complete form object, including its defaults.
 */
const applyFormChanges = (savedConfig, previousFormConfig, nextFormConfig) => {
  const config = { ...savedConfig };
  for (const key of new Set([...Object.keys(previousFormConfig), ...Object.keys(nextFormConfig)])) {
    if (isEqual(previousFormConfig[key], nextFormConfig[key])) continue;

    if (!(key in nextFormConfig)) {
      delete config[key];
    } else if (
      isPlainObject(savedConfig?.[key]) &&
      isPlainObject(previousFormConfig[key]) &&
      isPlainObject(nextFormConfig[key])
    ) {
      config[key] = applyFormChanges(
        savedConfig?.[key],
        previousFormConfig[key],
        nextFormConfig[key],
      );
    } else {
      config[key] = nextFormConfig[key];
    }
  }
  return config;
};

const mapVisualFormToSink = (config) => {
  const sink = {
    name: config.name,
    sink_config: {
      ...(config['ui.mode'] ? { ['ui.mode']: config['ui.mode'] } : {}),
    },
  };

  if (config.namespace) {
    sink.sink_config['database'] = config.namespace;
  }
  sink.sink_config['mapping'] = {
    mode: config.schemaMode !== 'name' ? 'auto' : 'manual',
    table_name_prefix: config.tablePrefix || '',
    table_name_suffix: config.tableSuffix || '',
    tracker_name: config.trackerEnabled ? config.trackerName || '__nexla_tracker_id' : '',
    tracker_mode: config.trackerEnabled ? 'RECORD' : 'NONE',
  };

  if (config.tableInsertMode) {
    sink.sink_config['insert.mode'] = config.tableInsertMode;
  }

  //TODO: We might not need these two
  if (config.schemaMode === 'name') {
    sink.sink_config['mapping']['exclude_tables'] = getTableNames(config.selection);
  }
  if (Object.values(config?.mapping || {}).length) {
    sink.sink_config['mapping']['overridden_mappings'] = getMapping(config.mapping);
  }
  return sink;
};

export const mapToSinkConfig = (config) => {
  if (isUiModeCode(config)) {
    return {
      name: config.name,
      sink_config: { ...config.sink_config, 'ui.mode': config['ui.mode'] },
    };
  }

  const sink = mapVisualFormToSink(config);
  /**
   * @Why?
   * Compare visual projections so defaults are not mistaken for user edits.
   */
  if (config.sink_config) {
    const previousFormConfig = parseSinkConfig({
      name: config.name,
      sink_config: config.sink_config,
    });
    const previousSinkConfig = mapVisualFormToSink({
      ...previousFormConfig,
      'ui.mode': undefined,
    }).sink_config;
    sink.sink_config = applyFormChanges(config.sink_config, previousSinkConfig, sink.sink_config);
  }
  return sink;
};

type MappingType = Record<string, string[]>;

export const getReverseMapping = (mapping: Record<string, { columns: string[] }>) => {
  if (!mapping) return null;

  return Object.keys(mapping).reduce((reverseMapping, tableName) => {
    const columns = mapping[tableName]?.columns || [];
    reverseMapping[tableName] = columns;
    return reverseMapping;
  }, {} as MappingType);
};

export const parseSinkConfig = (sink) => {
  const sinkConfig = sink?.sink_config;
  const config = {
    name: sink.name,
    sink_config: sinkConfig,
    namespace: sinkConfig?.['database'] || undefined,
    schemaMode: sinkConfig?.['mapping']?.mode !== 'auto' ? 'name' : 'auto',
    tablePrefix: sinkConfig?.['mapping']?.table_name_prefix || '',
    tableSuffix: sinkConfig?.['mapping']?.table_name_suffix || '',
    tableInsertMode: sinkConfig?.['insert.mode'] || 'UPSERT',
    trackerEnabled: sinkConfig?.['mapping']?.tracker_mode === 'RECORD' || false,
    trackerName: sinkConfig?.['mapping']?.tracker_name || '',
    ['ui.mode']: sinkConfig?.['ui.mode'],
    mapping: {},
    selection: {},
  };

  if (config.schemaMode === 'name') {
    config.selection = mapTablesToSelection(sinkConfig['mapping']?.exclude_tables);
  }

  if (sinkConfig['mapping']?.overridden_mappings) {
    config.mapping = getReverseMapping(sinkConfig['mapping']?.overridden_mappings);
  }

  return config;
};
